import { useState } from 'react';

import { cx } from '@libs/classnames';
import { useClickOutside } from '@libs/hooks/useClickOutside';
import { useOnEscape } from '@libs/hooks/useOnKeyDown';

import { MAX_ACTIVE_FRIEND_GAMES } from '@features/game/types/fast-game';
import { Button, Radio } from '@features/game/ui/PopupControls';
import stylesControls from '@features/game/ui/PopupControls/PopupControls.module.css';
import { components } from '@features/shared/api/fastgames/clients';

import { ITimeControlOption, TIME_CONTROLS } from './types';

import styles from './CreateFriendGameModal.module.css';

/*
    ____,-------------------------------,____
    \   |           Component           |   /
    /___|-------------------------------|___\
*/

const COLORS = [
  { title: 'За белых', value: 'White' as const },
  { title: 'Случайно', value: 'Random' as const },
  { title: 'За черных', value: 'Black' as const },
];

const RATING = [
  { title: 'На рейтинг', value: 'yes' as const },
  { title: 'Дружеская', value: 'no' as const },
];

type IColor = (typeof COLORS)[number]['value'];
type IRating = (typeof RATING)[number]['value'];

type Props = {
  className?: string;
  isMobile: boolean;
  isAuthenticated: boolean;
  initialPool: ITimeControlOption;
  initialColor?: IColor;
  initialRating?: IRating;
  myGamesCount?: number;
  playError?: Error | null;
  closeText?: string;
  onPlay: (cfg: components['schemas']['CreateInviteRequest'], pool: ITimeControlOption) => void;
  onMyGames?: () => void;
  onClose?: () => void;
};

export function CreateFriendGameModal({
  className,
  isMobile,
  isAuthenticated,
  initialPool,
  initialColor = 'Random',
  initialRating = 'yes',
  myGamesCount,
  playError,
  closeText,
  onPlay,
  onMyGames,
  onClose,
}: Props) {
  useOnEscape(onClose);

  const [selected, setSelected] = useState<ITimeControlOption | undefined>(initialPool);
  const [color, setColor] = useState<IColor>(initialColor);
  const [rating, setRating] = useState<IRating>(isAuthenticated ? initialRating : 'no');

  const ref = useClickOutside<HTMLElement>(() => onClose?.(), {
    enabled: !!onClose && !isMobile,
  });

  const handlePlay = () => {
    if (!selected) return;

    onPlay(
      {
        hostSide: color === 'Random' ? getRandomColor() : color,
        ratingGame: rating === 'yes',
        clockControlType: selected.clock.clockControlType,
        clockControlSettings: selected.clock.clockControlSettings,
      },
      selected,
    );
  };

  const isGamesLimit = (myGamesCount ?? 0) >= MAX_ACTIVE_FRIEND_GAMES;

  return (
    <article
      ref={ref}
      className={cx(styles.settings, stylesControls.container, className, {
        [stylesControls.is_mobile]: isMobile,
        [styles.is_mobile]: isMobile,
      })}
    >
      <div className={styles.header}>
        <h2 className={styles.title}>Игра с другом</h2>
      </div>

      <section className={styles.time_controls_grid}>
        <div className={styles.time_control_hint}>
          <p>Выберите контроль времени</p>
        </div>
        {TIME_CONTROLS.map((tc) => {
          const isSelected = selected === tc;

          return (
            <button
              key={tc.id}
              className={cx(styles.time_control_button, {
                [styles.is_selected]: isSelected,
              })}
              onClick={() => setSelected((prev) => (prev === tc ? undefined : tc))}
              type="button"
            >
              <div className={styles.time_control__top}>
                <span className={styles.time_control_type}>{tc.typeLabel}</span>
              </div>
              <span className={styles.time_control_label}>{tc.label}</span>
            </button>
          );
        })}
        <div className={styles.time_control_hint}>
          {isAuthenticated ? (
            rating === 'yes' ? (
              <p>Результат игры будет отражен в рейтинге</p>
            ) : (
              <p>Результат игры не влияет на рейтинг</p>
            )
          ) : (
            <p>Войдите, чтобы игры отражались в рейтинге</p>
          )}
        </div>
      </section>

      <div className={styles.group}>
        <Radio options={COLORS} selected={color} onChange={(o) => setColor(o.value)} />

        <Radio
          options={RATING}
          selected={rating}
          disabled={!isAuthenticated}
          onChange={(o) => setRating(o.value)}
        />
      </div>

      <div className={styles.group}>
        <Button onClick={handlePlay} disabled={!selected || isGamesLimit}>
          Создать игру
        </Button>
        {playError && <p className={styles.error}>Ошибка! Не удалось создать игру</p>}
        {isGamesLimit && (
          <p className={styles.error}>
            Вы превысили лимит одновременных игр: {MAX_ACTIVE_FRIEND_GAMES}шт
          </p>
        )}
        <Button onClick={onMyGames} customAlpha={10} disabled={!onMyGames}>
          Мои игры
        </Button>
      </div>

      {isMobile && onClose && (
        <Button ghost onClick={onClose}>
          {closeText ?? 'Закрыть'}
        </Button>
      )}
    </article>
  );
}

function getRandomColor() {
  return Math.random() > 0.5 ? 'White' : 'Black';
}
